"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { readConsent, CONSENT_EVENT } from "@/lib/consent";
import { useAuth } from "@/context/AuthContext";
import { splitName } from "@/lib/klaviyo-core";
import { ensureKlaviyoStub, klaviyoIdentify } from "@/lib/klaviyo-client";

const COMPANY_ID = process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID;

/**
 * Klaviyo (e-mailmarketing/onsite-tracking). AVG-veilig: laadt PAS na
 * marketing-toestemming (zelfde categorie als Meta Pixel/GTM, zie
 * lib/consent.ts) — trekt de bezoeker toestemming in, dan stopt het laden
 * bij de volgende paginaweergave.
 *
 * De wachtrij-stub (window.klaviyo) en de events zelf (Viewed Product, Added
 * to Cart, Started Checkout) zitten in lib/klaviyo-client.ts, met dezelfde
 * toestemmingscheck. Hier alleen nog: klaviyo.js laden en een ingelogde klant
 * identificeren (e-mail + naam uit het account, geen telefoon), zodat diens
 * events bij het juiste profiel komen.
 */
export default function Klaviyo() {
  const [allowed, setAllowed] = useState(false);
  // MOW-PERF-OKT26: alleen meelezen, zodat dit onderdeel (op elke pagina) de Auth-SDK niet zelf laadt.
  const { user } = useAuth("meelezen");
  const email = user?.email;
  const displayName = user?.displayName;

  useEffect(() => {
    const update = () => {
      const ok = !!readConsent()?.marketing;
      // Stub vóór klaviyo.js, zodat eerdere aanroepen in de wachtrij komen.
      if (ok) ensureKlaviyoStub();
      setAllowed(ok);
    };
    update();
    window.addEventListener(CONSENT_EVENT, update);
    return () => window.removeEventListener(CONSENT_EVENT, update);
  }, []);

  useEffect(() => {
    if (!allowed || !email) return;
    const { first, last } = splitName(displayName);
    klaviyoIdentify({ email, first_name: first, last_name: last });
  }, [allowed, email, displayName]);

  if (!COMPANY_ID || !allowed) return null;

  return (
    <Script
      async
      strategy="afterInteractive"
      src={`https://static.klaviyo.com/onsite/js/${COMPANY_ID}/klaviyo.js?company_id=${COMPANY_ID}`}
    />
  );
}
